import { useMemo } from 'react';

import {
  TALENT_DISCOVERY_MAP_DATA_V3,
  type TalentDiscoveryReqV3,
  type TalentDiscoveryRespGeoDataV3,
} from '@revelio/data-access';

import { useIsTaxonomyResolved } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { useRequestGeographyTaxonomyId } from '../../filters/hooks/useRequestGeographyTaxonomyId';
import type { MapPoint } from '../Chart/Map/types';
import { buildTalentDiscoveryFiltersV3 } from './talentDiscovery/adapter';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';
import { transformTalentDiscoveryMapData } from './utils/talent-discovery-map-utils';

export const TalentDiscoveryMapDataProvider = ({
  children,
}: {
  children: DataProviderChildren<{ points: MapPoint[] } | undefined>;
}) => {
  const filters = useEffectiveFilters();
  const geographyTaxonomyId = useRequestGeographyTaxonomyId();

  // Build v3 filters and variables
  const v3Variables: { query: TalentDiscoveryReqV3 } = useMemo(() => {
    const builtQuery = buildTalentDiscoveryFiltersV3(
      filters,
      geographyTaxonomyId
    );
    return {
      query: builtQuery,
    };
  }, [filters, geographyTaxonomyId]);

  // Check if we have any meaningful filters (avoid querying with empty expr)
  const hasFilters = useMemo(() => {
    const expr = v3Variables.query.filters.expr;
    if (!expr) return false;

    if (expr.user_and) {
      return expr.user_and.children.length > 0;
    }

    if (expr.user_or) {
      return expr.user_or.children.length > 0;
    }

    if (expr.user_value) {
      return true;
    }

    if (expr.user_not) {
      return true;
    }

    return false;
  }, [v3Variables]);

  // V3 query
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const [{ data: v3Data, status }] = useUrqlQueryWithStatus({
    query: TALENT_DISCOVERY_MAP_DATA_V3,
    variables: v3Variables,
    pause: !hasFilters || !isTaxonomyResolved,
  });

  const points: MapPoint[] = useMemo(() => {
    const resp = v3Data?.talent_discovery_search_v3;
    const raw = resp?.map_data ?? [];
    return transformTalentDiscoveryMapData(
      raw as (TalentDiscoveryRespGeoDataV3 | null)[]
    );
  }, [v3Data]);

  const finalStatus = hasFilters
    ? (status ?? DataProviderQueryStatus.Idle)
    : DataProviderQueryStatus.Idle;

  const result = {
    data: hasFilters ? { points } : undefined,
    status: finalStatus,
  };

  return children(result);
};
